"use client";

/**
 * The pill's key on a task's screen (spec 2026-09-20 one-composer-everywhere,
 * R-F3): the SAME key, in the same seat, as on every other screen: the design
 * system's Button wearing PILL_KEY_CLASS, exactly what home renders. Only what
 * it reads changes, with the moment.
 *
 * R-F10 (lead ruling, 2026-09-20), which overrides the single-state reading of
 * P-9: WHILE A TURN IS IN FLIGHT, STOP IS ALWAYS ONE CLICK AWAY. Running work
 * with no way to stop it is the one thing this key must never leave, so the
 * table is:
 *
 *   nothing in flight                 -> Delegate (shut, with its reason, when
 *                                        the lock is shut or the box is empty;
 *                                        shut and reading Uploading while a
 *                                        file still uploads)
 *   in flight, nothing to send        -> Stop, enabled, even when the lock is shut
 *   in flight, words, the lock shut   -> Stop, enabled; a Queue is impossible,
 *   or a file still uploading            so it is not offered
 *   in flight, words, the lock open   -> the task box's own split in the SAME
 *                                        slot: Queue beside the stop square,
 *                                        two buttons in one visual key
 *
 * Stop is never disabled in any in-flight state: a shut lock is about what may
 * be SENT. Presentational: BashPill hands it the facts.
 */
import type { ReactElement } from "react";
import { ConnectionHints } from "@/components/message-input/ConnectionHints";
import { Button } from "@/components/ui/button";
import { PILL_KEY_CLASS } from "../pillChrome";
import { DelegateFace } from "./UploadingFace";

/**
 * The split is two buttons in one visual key, so it cannot BE the Button; it
 * wears the Button's own box instead (size "sm": the 10 radius, a 1px border,
 * and on the Queue segment the same `py-2 text-3.5`), so the slot never changes
 * height or corner when the key flips between Delegate, Stop and the split. The
 * task box's `.run-split` chrome (globals.css) makes the two verbs read as one
 * squared control; it is unlayered, so the few values that must match the Button
 * are marked important.
 */
const SPLIT_CLASS =
  "bh-key run-split flex-none rounded-10! border border-transparent";
const QUEUE_SEGMENT_BOX = "py-2! text-3.5!";

/**
 * `.run-split` clips to its rounded box, which would swallow the global focus
 * ring (drawn 2px OUTSIDE the segment). Drawing it inside keeps every segment
 * visibly reachable by keyboard.
 */
const SEGMENT_RING = "focus-visible:-outline-offset-2!";

export function PillTaskKey({
  inFlight,
  hasDraft,
  blocked,
  reason,
  deployPaused,
  offline,
  waitReason,
  onSend,
  onStop,
}: {
  /** A turn is in flight on what the column shows. */
  inFlight: boolean;
  /** There is something to send: words, or a finished attachment. */
  hasDraft: boolean;
  /** The lock is shut. */
  blocked: boolean;
  /** Why, for the key's title. */
  reason: string | undefined;
  deployPaused: boolean;
  offline: boolean;
  /** A file is still uploading: the key is shut, reads Uploading, and names it (spec 2026-09-24, Part B). */
  waitReason?: string;
  onSend: () => void;
  onStop: () => void;
}): ReactElement {
  /** Both verbs are live: the only state that earns two seats. A file still
   *  uploading shuts the Queue as it shuts Delegate. */
  const queueAndStop = inFlight && hasDraft && !blocked && !waitReason;
  /** Every other in-flight state: the one verb that still works is Stop. */
  const stopOnly = inFlight && !queueAndStop;
  return (
    <>
      <ConnectionHints deployPaused={deployPaused} offline={offline} />
      {queueAndStop ? (
        <div className={SPLIT_CLASS} data-testid="pill-task-key-split">
          <button
            type="button"
            className={`run-split__queue ${QUEUE_SEGMENT_BOX} ${SEGMENT_RING}`}
            aria-label="Queue message"
            title="Queue message"
            onClick={onSend}
          >
            {/* The pill's own mark, so the split still reads as the pill's key. */}
            <b className="font-mono text-3 font-semibold text-gold-molten-hi">
              ↵
            </b>
            <span>Queue</span>
          </button>
          <span className="run-split__sep" aria-hidden="true" />
          <button
            type="button"
            className={`run-split__stop ${SEGMENT_RING}`}
            aria-label="Stop"
            title="Stop"
            onClick={onStop}
          >
            <span className="run-split__square" aria-hidden="true" />
          </button>
        </div>
      ) : stopOnly ? (
        <Button
          size="sm"
          type="button"
          className={PILL_KEY_CLASS}
          aria-label="Stop generation"
          title="Stop"
          onClick={onStop}
        >
          {/* The task box's own stop square, in the key's ink. */}
          <span className="run-split__square" aria-hidden="true" /> Stop
        </Button>
      ) : (
        <Button
          size="sm"
          type="button"
          className={PILL_KEY_CLASS}
          aria-label={waitReason ?? "Delegate"}
          title={blocked ? reason : waitReason}
          disabled={!hasDraft || blocked || Boolean(waitReason)}
          onClick={onSend}
        >
          <DelegateFace waitReason={waitReason} />
        </Button>
      )}
    </>
  );
}
